import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Modal from "./UI/Modal";
import { teamApi } from "../api/client";

const PlaceholderMember = () => (
  <div className="animate-pulse p-6 bg-white/60 dark:bg-gray-900/60 rounded-2xl backdrop-blur-sm border border-white/10">
    <div className="mx-auto h-20 w-20 rounded-full bg-gray-100 dark:bg-gray-700 mb-4" />
    <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4 mx-auto mb-2" />
    <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-1/2 mx-auto" />
  </div>
);

const RoleBadge = ({ role }) => (
  <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold uppercase bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white shadow-sm tracking-wide">
    {role}
  </span>
);

const SocialIcon = ({ kind, url }) => {
  if (!url) return null;
  const common = "h-5 w-5";
  if (kind === "twitter")
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-gray-500 hover:text-blue-400"
      >
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M23 4.56c-.8.35-1.66.58-2.56.69a4.48 4.48 0 0 0 1.97-2.48c-.88.52-1.86.9-2.9 1.1A4.48 4.48 0 0 0 16.1 3c-2.48 0-4.5 2.02-4.5 4.5 0 .35.04.7.11 1.03C7.69 8.4 4.07 6.54 1.64 3.14c-.38.65-.6 1.4-.6 2.2 0 1.52.77 2.86 1.94 3.64-.72-.02-1.4-.22-1.99-.55v.06c0 2.12 1.5 3.9 3.48 4.3-.36.1-.74.15-1.13.15-.28 0-.55-.03-.82-.08.55 1.72 2.15 2.98 4.05 3.02A9 9 0 0 1 1 19.54 12.7 12.7 0 0 0 7.29 21c8.76 0 13.56-7.26 13.56-13.56v-.62A9.6 9.6 0 0 0 23 4.56z" />
        </svg>
      </a>
    );
  if (kind === "linkedin")
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-gray-500 hover:text-blue-600"
      >
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M4.98 3.5C4.98 4.88 3.86 6 2.48 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM.2 8.98h4.5V24H.2V8.98zM8.98 8.98h4.32v2.06h.06c.6-1.14 2.06-2.34 4.24-2.34 4.54 0 5.38 2.98 5.38 6.84V24h-4.5v-7.5c0-1.78-.03-4.06-2.48-4.06-2.48 0-2.86 1.94-2.86 3.94V24h-4.5V8.98z" />
        </svg>
      </a>
    );
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-gray-500 hover:text-gray-600"
    >
      <svg
        className={common}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
      </svg>
    </a>
  );
};

const TeamSection = ({ limit = 8 }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReducedMotion(!!(mq && mq.matches));
    handler();
    if (mq && mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq && mq.addListener) mq.addListener(handler);
    return () => {
      if (mq && mq.removeEventListener)
        mq.removeEventListener("change", handler);
      else if (mq && mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await teamApi.list({ limit });
        if (!mounted) return;
        const payload = res.data;
        setMembers(Array.isArray(payload) ? payload : payload?.members || []);
      } catch (e) {
        console.error("Failed to load team", e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [limit]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {Array.from({ length: Math.min(limit, 8) }).map((_, i) => (
          <PlaceholderMember key={i} />
        ))}
      </div>
    );
  }

  if (!members.length)
    return (
      <div className="py-8 text-center text-gray-600 dark:text-gray-400">
        <h3 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-600">
          Meet our team
        </h3>
        No team members yet.
      </div>
    );

  return (
    <section className="py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h3 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-600">
            Meet our team
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            A team of passionate people building delightful payment experiences.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {members.map((m, idx) => (
            <motion.div
              key={m._id}
              initial={reducedMotion ? undefined : { opacity: 0, y: 8 }}
              whileInView={reducedMotion ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.18 }}
              whileHover={reducedMotion ? undefined : { y: -8, scale: 1.02 }}
              transition={
                reducedMotion
                  ? undefined
                  : {
                      type: "spring",
                      stiffness: 260,
                      damping: 20,
                      delay: idx * 0.04,
                    }
              }
              className="group relative p-6 rounded-2xl bg-white/40 dark:bg-gray-900/50 backdrop-blur-md border border-white/10 hover:shadow-2xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400"
              onClick={() => setActive(m)}
              tabIndex={0}
              role="button"
              aria-label={`Open profile for ${m.name}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActive(m);
                }
              }}
            >
              {/* decorative blurred accent */}
              <div className="pointer-events-none absolute -top-6 -right-6 h-28 w-28 rounded-full bg-gradient-to-tr from-pink-400/30 to-indigo-400/20 blur-3xl transform-gpu opacity-60" />

              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  {/* gradient ring */}
                  <div className="rounded-full p-0.5 bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-rose-400">
                    <div className="h-16 w-16 rounded-full overflow-hidden bg-white/60 dark:bg-gray-800/60">
                      {m.imageUrl ? (
                        <img
                          src={m.imageUrl}
                          alt={m.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 font-bold">
                          {m.name ? m.name.charAt(0).toUpperCase() : "T"}
                        </div>
                      )}
                    </div>
                  </div>
                  {m.isLead && (
                    <div className="absolute -bottom-2 -right-2 bg-amber-400 text-white rounded-full px-2 text-xs font-semibold shadow">
                      Lead
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-base sm:text-lg font-extrabold text-primary tracking-tight">
                        {m.name}
                      </div>
                      {m.position && (
                        <div className="text-xs uppercase tracking-widest text-indigo-600/80 mt-1 font-medium">
                          {m.position}
                        </div>
                      )}
                    </div>
                    <div className="space-x-2 flex items-center">
                      {/* social icons visible on hover/focus */}
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200">
                        <SocialIcon kind="twitter" url={m.twitter} />
                        <SocialIcon kind="linkedin" url={m.linkedin} />
                      </div>
                    </div>
                  </div>

                  {m.tags && m.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {m.tags.slice(0, 3).map((t, i) => (
                        <RoleBadge key={i} role={t} />
                      ))}
                    </div>
                  )}

                  {m.bio && (
                    <div
                      className="text-sm sm:text-base text-gray-700 dark:text-gray-200 mt-3 leading-relaxed line-clamp-3"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {m.bio}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* 
        <div className="mt-8 text-center">
          <button
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-indigo-600 to-sky-500 text-white font-semibold shadow-lg"
            onClick={() => window.location.assign("/team")}
          >
            See full team
          </button>
        </div> */}

        <Modal
          isOpen={!!active}
          onClose={() => setActive(null)}
          title={active?.name}
          size="md"
        >
          {active && (
            <div className="space-y-4 text-center max-w-xl mx-auto">
              <div className="mx-auto h-28 w-28 rounded-full overflow-hidden bg-gray-50 dark:bg-gray-900 ring-2 ring-white/20">
                {active.imageUrl ? (
                  <img
                    src={active.imageUrl}
                    alt={active.name}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </div>
              <div className="text-sm uppercase tracking-widest text-indigo-600/80 font-medium">
                {active.position}
              </div>
              <div className="text-base text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {active.bio}
              </div>

              <div className="flex items-center justify-center gap-4 mt-2">
                <SocialIcon kind="twitter" url={active.twitter} />
                <SocialIcon kind="linkedin" url={active.linkedin} />
              </div>
            </div>
          )}
        </Modal>
      </div>
    </section>
  );
};

export default TeamSection;
